import { Panel, Container, Label, Button } from '@playcanvas/pcui/react';
import React, { useState, useEffect, useRef } from 'react';
import { SetProperty, ObserverData } from '../../types';
import { Slider } from '../components';

const AnimationControlsPanel: React.FC<{
    observerData: ObserverData,
    setProperty: SetProperty
}> = ({ observerData, setProperty }) => {
    const [collapsed, setCollapsed] = useState(true);  // Start collapsed (closed) by default
    const panelRef = useRef<any>(null);
    
    const aircraftConfig = observerData.aircraftConfig?.configuration;
    const hasConfig = aircraftConfig && (aircraftConfig.animationDefinitions || aircraftConfig.AnimationDefinitions);

    useEffect(() => {
        // Ensure panel starts collapsed after it's fully initialized
        setTimeout(() => {
            if (panelRef.current) {
                // Try to access the underlying PCUI panel and set collapsed
                const pcuiPanel = panelRef.current.pcuiPanel || panelRef.current;
                if (pcuiPanel && typeof pcuiPanel.collapsed !== 'undefined') {
                    pcuiPanel.collapsed = true;
                }
            }
        }, 0);
    }, []);

    if (!hasConfig) {
        return null;
    }

    return (
        <Panel ref={panelRef} headerText='ANIMATION CONTROLS' class='animation-controls-panel' collapsible={true} flexShrink={'0'} flexGrow={'0'} collapsed={collapsed}>
            <Label text='Control Surfaces' class='section-header' />
            <Slider
                label='Ailerons'
                precision={2}
                min={0}
                max={1}
                value={0.5}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('ailerons', value);
                }} />
            <Slider
                label='Elevator'
                precision={2}
                min={0}
                max={1}
                value={0.5}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('elevator', value);
                }} />
            <Slider
                label='Rudder'
                precision={2}
                min={0}
                max={1}
                value={0.5}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('rudder', value);
                }} />
            <Slider
                label='Nose Wheel'
                precision={2}
                min={0}
                max={1}
                value={0.5}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('nosewheel', value);
                }} />
            
            <Label text='Landing Gear' class='section-header' />
            <Slider
                label='Gear'
                precision={2}
                min={0}
                max={1}
                value={1}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('gear', value);
                }} />
            <Slider
                label='Suspension'
                precision={2}
                min={0}
                max={1}
                value={1}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('suspension', value);
                }} />
            
            <Label text='High-Lift Devices' class='section-header' />
            <Slider
                label='Flaps'
                precision={2}
                min={0}
                max={1}
                value={0}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('flaps', value);
                }} />
            <Slider
                label='Spoilers'
                precision={2}
                min={0}
                max={1}
                value={0}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('spoilers', value);
                }} />
            
            <Label text='Engines' class='section-header' />
            <Slider
                label='Throttle'
                precision={2}
                min={0}
                max={1}
                value={0}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('throttle', value);
                }} />
            
            <Label text='Wing Flex' class='section-header' />
            <Slider
                label='Wing Flex'
                precision={2}
                min={0}
                max={1}
                value={0.5}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('wingflex', value);
                }} />
            
            <Label text='Special Actions' class='section-header' />
            <Slider
                label='Doors'
                precision={2}
                min={0}
                max={1}
                value={0}
                setProperty={(value: number) => {
                    if (window.viewer) (window.viewer as any).setAnimationGroup('doors', value);
                }} />
            
            <Container class='animation-controls-buttons' flexDirection='column'>
                <Button
                    class='secondary'
                    text='RESET ALL TO NEUTRAL'
                    onClick={() => {
                        if (window.viewer) {
                            const viewer = window.viewer as any;
                            viewer.setAnimationGroup('ailerons', 0.5);
                            viewer.setAnimationGroup('elevator', 0.5);
                            viewer.setAnimationGroup('rudder', 0.5);
                            viewer.setAnimationGroup('nosewheel', 0.5);
                            viewer.setAnimationGroup('flaps', 0);
                            viewer.setAnimationGroup('spoilers', 0);
                            viewer.setAnimationGroup('throttle', 0);
                            viewer.setAnimationGroup('wingflex', 0.5);
                            viewer.setAnimationGroup('suspension', 1.0);
                            viewer.setAnimationGroup('doors', 0);
                        }
                    }}
                />
                <Button
                    class='secondary'
                    text='APPLY SCREENSHOT SETTINGS'
                    onClick={() => {
                        if (window.viewer) (window.viewer as any).applyScreenshotAnimationSettings();
                    }}
                />
            </Container>
        </Panel>
    );
};

export default AnimationControlsPanel;

